import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Flex, Text } from 'rebass/styled-components';
import { Loader, Button } from '@getro/rombo';
import { ChevronsDown, ChevronsUp } from 'lucide-react';
import { IntroPathCard } from './IntroPathCard';
import { useIntroPaths } from './hooks/useIntroPaths';
import { useHasRelationshipStrengthFeature } from '../../../hooks/useHasRelationshipStrengthFeature';
import { trackEvent } from '../../../helpers/analytics';
import { getContactDisplayName } from '../../../helpers/getContactDisplayName';

export const IntroPaths = ({ network, contact }) => {
  const hasRelationshipStrength = useHasRelationshipStrengthFeature({ network });
  const {
    data = [],
    isLoading,
    isSuccess,
    showAll,
    setShowAll,
    itemsToRender,
    hasMore,
    expandAll,
    setExpandAll,
  } = useIntroPaths({ contact, hasRelationshipStrength });
  const [cardOverrides, setCardOverrides] = useState({});

  // Per-card expand overrides are keyed by path id; clear them when the viewed
  // contact changes so expansion state never leaks across contacts.
  useEffect(() => {
    setCardOverrides({});
  }, [contact.id]);

  useEffect(() => {
    if (isSuccess) {
      trackEvent('contacts:intro_paths:view', {
        contactId: contact.id,
        collectionId: network?.id,
        introPathsCount: data.length,
        hasRelationshipStrength,
        viewSetting: expandAll,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, contact.id]);

  if (isLoading) {
    return (
      <Flex alignItems="center" justifyContent="center" py="24px">
        <Loader />
      </Flex>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Text fontSize="14px" color="text.main">
        There are no signals that suggest your team can reach {getContactDisplayName(contact, 'this contact')}.
      </Text>
    );
  }

  if (hasRelationshipStrength) {
    const allExpanded = expandAll === 'expanded';
    const resolveExpanded = (id) => (cardOverrides[id] !== undefined ? cardOverrides[id] : allExpanded);

    const toggleAll = () => {
      const next = allExpanded ? 'collapsed' : 'expanded';
      setExpandAll(next);
      setCardOverrides({}); // clear per-card overrides so the global preference applies uniformly
      trackEvent('contacts:intro_paths:all_toggled', {
        contactId: contact.id,
        action: next === 'expanded' ? 'expand' : 'collapse',
        pathCount: data.length,
      });
    };

    return (
      <Flex sx={{ flexDirection: 'column', gap: '8px' }}>
        <Flex alignItems="center" sx={{ gap: '16px' }}>
          <Text flex="1 0 0" fontSize="14px">
            {data.length} intro {data.length === 1 ? 'path' : 'paths'}
          </Text>
          <Button
            variant="tertiary"
            size="small"
            type="button"
            icon={allExpanded ? ChevronsUp : ChevronsDown}
            onClick={toggleAll}
          >
            {allExpanded ? 'Collapse all' : 'Expand all'}
          </Button>
        </Flex>

        <Flex sx={{ flexDirection: 'column', gap: '8px' }}>
          {itemsToRender.map((item) => (
            <IntroPathCard
              key={item.id}
              network={network}
              item={item}
              contact={contact}
              expanded={resolveExpanded(item.id)}
              onExpandedChange={(value) => setCardOverrides((prev) => ({ ...prev, [item.id]: value }))}
            />
          ))}
        </Flex>

        {hasMore && !showAll && (
          <Box textAlign="center" pt="8px">
            <Button variant="secondary" size="small" onClick={() => setShowAll(true)}>
              View more
            </Button>
          </Box>
        )}
      </Flex>
    );
  }

  // Flag off — today's legacy view, unchanged.
  return (
    <Flex sx={{ flexDirection: 'column', gap: '12px' }}>
      <Box>
        <Flex sx={{ flexDirection: 'column', gap: '12px' }}>
          {itemsToRender.map((item) => (
            <IntroPathCard key={item.id} network={network} item={item} contact={contact} />
          ))}
        </Flex>
      </Box>

      {hasMore && (
        <Box textAlign="center" pt="16px">
          {!showAll && (
            <Button variant="secondary" size="small" onClick={() => setShowAll(true)}>
              View more
            </Button>
          )}
        </Box>
      )}
    </Flex>
  );
};

IntroPaths.propTypes = {
  contact: PropTypes.object.isRequired,
  network: PropTypes.object.isRequired,
};
